import { CommonModule, Location } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    computed,
    inject,
    NgZone,
    OnInit,
    Signal,
    signal,
    ViewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { debounceTime, merge, take } from 'rxjs';
import * as XLSX from 'xlsx';
import { membershipMap, MemberType, ValidityStatus } from '../../../models/enums';
import { Organisation } from '../../../models/organisation';
import { InitiativeService } from '../../../services/initiative.service';
import { OrganisationService } from '../../../services/organisation.service';
import { ProjectService } from '../../../services/project.service';
import { ReferenceDataService } from '../../../services/reference-data.service';
import { TableStateService } from '../../../services/table-state.service';
import { iso3ToEmojiFlag } from '../../../utils/country-utils';
import { getGridTemplate, mapObject, MY_DATE_FORMATS } from '../../../utils/utils';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import {
    AdvancedSelectComponent,
    AdvancedSelectValue,
    createAdvancedSelectValue
} from '../../misc/advanced-select/advanced-select.component';
import { ColumnSelectorDialogComponent } from '../../misc/column-selector-dialog/column-selector-dialog.component';
import { mapToNestedOptions } from '../../misc/nested-options';
import { ExportService } from '../../../services/export.service';
import { DateTime } from 'luxon';


interface OrganisationFilters {
    textFilter: string;
    stakeholderGroups: AdvancedSelectValue;
    projects: AdvancedSelectValue;
    initiatives: AdvancedSelectValue;
    topics: AdvancedSelectValue;
    audiences: AdvancedSelectValue;
    memberTypes: AdvancedSelectValue;
    countries: AdvancedSelectValue;
    showActiveItems: boolean;
    showArchivedItems: boolean;
}


@Component({
    selector: 'app-organisations',
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatPaginatorModule,
        MatSortModule,
        MatTableModule,
        MatToolbarModule,
        ReactiveFormsModule,
        MatIconModule,
        MatExpansionModule,
        MatChipsModule,
        MatDatepickerModule,
        MatTooltipModule,
        AdvancedSelectComponent,
        MatProgressSpinnerModule,
        MatCheckboxModule
    ],
    providers: [
        provideLuxonDateAdapter(),
        { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
        { provide: MAT_DATE_LOCALE, useValue: 'fr' },
    ],
    templateUrl: './organisations.component.html',
    styleUrl: './organisations.component.scss'
})
export class OrganisationsComponent implements OnInit, AfterViewInit {
    readonly iso3ToEmojiFlag = iso3ToEmojiFlag;
    readonly ValidityStatus = ValidityStatus;
    tableId = 'organisations'
    searchText = '';
    loading: boolean = false;

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;
    @ViewChild('filtersPanel') filtersPanel!: MatExpansionPanel;

    allOrganisations = signal<Organisation[]>([]);

    dataSource = new MatTableDataSource<Organisation>();
    allColumns = [
        { key: 'valid', label: 'Valid', type: 'icon' },
        { key: 'member', label: 'Member', type: 'icon' },
        { key: 'supplier', label: 'Supplier', type: 'icon' },
        { key: 'name', label: 'Full name', type: 'longtext' },
        { key: 'acronym', label: 'Short name', type: 'text' },
        { key: 'stakeholderGroups', label: 'Stakeholder groups', type: 'chips' },
        { key: 'projects', label: 'Projects', type: 'chips' },
        { key: 'initiatives', label: 'Initiatives', type: 'chips' },
        { key: 'topics', label: 'Topics of interest', type: 'chips' },
        { key: 'targetAudiences', label: 'Target audiences', type: 'chips' },
        // { key: 'membershipType', label: 'Membership type', type: 'text' },
        { key: 'countries', label: 'Country', type: 'single-chip' },
        { key: 'lastModifiedOn', label: 'Last modified on', type: 'text' },
    ];
    columnLabelMap: Record<string, string> = {};

    defaultSelectedColumns: string[] = ['valid', 'member', 'supplier', 'acronym', 'name', 'stakeholderGroups', 'topics', 'targetAudiences'];
    defaultColumnsOrder: string[] = this.allColumns.map(v => v.key);
    selectedColumns: string[] = this.defaultSelectedColumns;
    pageEvent: PageEvent | undefined;

    readonly showAllOrAny: Signal<boolean> = signal(false);

    readonly refDataService = inject(ReferenceDataService);
    readonly organisationService = inject(OrganisationService);
    readonly tableStateService = inject(TableStateService);
    readonly initiativeService = inject(InitiativeService);
    readonly projectService = inject(ProjectService);
    readonly exportService = inject(ExportService);

    readonly allStakeholderGroups = this.refDataService.getOrgStakeholderGroups();
    readonly allStakeholderGroupOptions = computed(() => mapToNestedOptions(this.allStakeholderGroups(), true));
    readonly allProjects = toSignal(this.projectService.getAllProjects(), { initialValue: [] });
    readonly allProjectOptions = computed(() => mapToNestedOptions(this.allProjects(), true));
    readonly allInitiatives = toSignal(this.initiativeService.getAllInitiatives(), { initialValue: [] });
    readonly allInitiativeOptions = computed(() => mapToNestedOptions(this.allInitiatives(), true))
    readonly allGeneralTopics = this.refDataService.getGeneralTopicsOfInterest();
    readonly allMemberTopics = this.refDataService.getMemberTopicsOfInterest();
    readonly generalTopicIds = computed(() => this.allGeneralTopics().map(v => v.id));
    readonly memberTopicIds = computed(() => this.allMemberTopics().map(v => v.id));
    readonly allTopicOptions = computed(() => {
        const topics = [
            ...this.allGeneralTopics().map(t => ({
                ...t,
                categoryId: 1,
                categoryName: 'General topics'
            })),
            ...this.allMemberTopics().map(t => ({
                ...t,
                categoryId: 2,
                categoryName: 'Member topics'
            }))
        ];

        return mapToNestedOptions(topics, true);
    });

    readonly allAudiences = this.refDataService.getAudiences();
    readonly allAudienceOptions = computed(() => mapToNestedOptions(this.allAudiences(), true));
    // readonly allMemberTypeOptions = enumToSortedNestedOptions(MemberType);
    readonly allMemberTypeOptions = [
        { value: MemberType.FULL, label: 'Full', isGroup: false },
        { value: MemberType.ASSOCIATE, label: 'Associate', isGroup: false },
    ];
    readonly countries = this.refDataService.getCountries();
    readonly countryNames = computed(() => {
        const list = this.countries();
        return new Map(list.map(c => [c.isoCode, c.enName]));
    });
    readonly allCountryOptions = computed(() => this.countries().map(v =>
    ({
        value: v.isoCode,
        label: iso3ToEmojiFlag(v.isoCode) + ' ' + v.enName,
        isGroup: false,
        flag: iso3ToEmojiFlag(v.isoCode)
    })));

    filterForm: FormGroup;
    filters: OrganisationFilters = this.getDefaultFilters();
    filtersExpanded: boolean = true;

    resetCounter = 0; // used to reset the filter form

    constructor(private readonly dialog: MatDialog, private readonly snackBar: MatSnackBar, private readonly router: Router,
        private readonly location: Location, private readonly cdr: ChangeDetectorRef, private readonly zone: NgZone,
        private readonly formBuilder: FormBuilder) {
        this.filterForm = {} as FormGroup;
    }

    ngOnInit(): void {
        this.columnLabelMap = Object.fromEntries(
            this.allColumns.map(col => [col.key, col.label])
        );
        this.loadOrganisations();

        // Display and clear any navigation message
        const state = this.location.getState() as { message?: string };
        if (state?.message) {
            this.snackBar.open(state.message, 'Close', {
                duration: 3000,
                panelClass: ['snackbar-success']
            });
            // Clear the message from history so it doesn't persist on reload
            history.replaceState({}, document.title, location.pathname);
        }

        this.filterForm = this.formBuilder.nonNullable.group<OrganisationFilters>({
            textFilter: '',
            stakeholderGroups: { selectedValues: [], matchAll: false },
            projects: { selectedValues: [], matchAll: false },
            initiatives: { selectedValues: [], matchAll: false },
            topics: { selectedValues: [], matchAll: false },
            audiences: { selectedValues: [], matchAll: false },
            memberTypes: { selectedValues: [], matchAll: false },
            countries: { selectedValues: [], matchAll: false },
            showActiveItems: true,
            showArchivedItems: false
        });
    }

    ngAfterViewInit() {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        // this.dataSource.filterPredicate = this.filterFunction;
        this.dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {
                case 'valid':
                    // return ((item.name && item.acronym && ((item.stakeholderGroups && item.stakeholderGroups.length > 0) || item.supplierDetails)));
                    return item.validityStatus();
                case 'name':
                    return item.name.toLowerCase() ?? 'N/A'; // Ensure case-insensitive sorting
                case 'acronym':
                    return item.acronym?.toLowerCase() ?? 'N/A'; // Ensure case-insensitive sorting
                case 'member':
                    return item.membershipStatus;
                case 'supplier':
                    return (item.supplierDetails ? 'true' : 'false');
                // case 'membershipType':
                //     return item.memberDetails?.memberType ?? 'N/A';
                case 'lastModifiedOn':
                    return item.lastModifiedOn ?? 'N/A';
                default:
                    return (item as Record<string, any>)[property];
            }
        }

        const saved = this.tableStateService.getState<OrganisationFilters>(this.tableId);
        if (saved) {
            // console.debug('restoring', saved)
            this.zone.onStable.pipe(take(1)).subscribe(() => {
                this.paginator.pageIndex = saved.pageIndex;
                this.paginator.pageSize = saved.pageSize;
                this.sort.active = saved.sortActive;
                this.sort.direction = saved.sortDirection;
                this.filterForm.patchValue(saved.filters ? saved.filters : this.getDefaultFilters());
                this.filtersExpanded = saved.filtersExpanded;
                this.selectedColumns = saved.selectedColumnKeys ?? this.defaultSelectedColumns;
                if (saved.allColumnKeys) {
                    this.allColumns.sort(
                        (a, b) => saved.allColumnKeys.indexOf(a.key) - saved.allColumnKeys.indexOf(b.key)
                    );
                }
                this.cdr.detectChanges();
            });
        }

        merge(
            this.filtersPanel.expandedChange,
            this.paginator.page,
            this.sort.sortChange,
            this.filterForm.valueChanges.pipe(debounceTime(300))
        ).subscribe(() => {
            this.saveState();
        });
        this.filterForm.valueChanges
            .pipe(debounceTime(300))
            .subscribe(filters => {
                const filtered = this.allOrganisations().filter(org =>
                    this.filterFunction(org, JSON.stringify(this.filterForm.getRawValue()))
                );
                this.dataSource.data = filtered;
            });
    }

    get displayedColumns(): string[] {
        return this.selectedColumns;
    }

    loadOrganisations(): void {
        this.loading = true;
        this.organisationService.getAllOrganisations().subscribe({
            next: organisations => {
                this.allOrganisations.set(organisations);
                const filtered = this.allOrganisations().filter(org =>
                    this.filterFunction(org, JSON.stringify(this.filterForm.getRawValue()))
                );
                this.dataSource.data = filtered;
                this.loading = false;
            },
            error: msg => {
                console.error('Failed to load organisations:', msg);
                this.snackBar.open('Failed to load organisations', 'Close', {
                    duration: 3000,
                    panelClass: ['snackbar-error']
                });
                this.loading = false;
            }
        });
    }

    saveState() {
        const state = {
            pageIndex: this.paginator?.pageIndex ?? 0,
            pageSize: this.paginator?.pageSize ?? 10,
            sortActive: this.sort?.active,
            sortDirection: this.sort?.direction,
            filters: this.filterForm.getRawValue(),
            filtersExpanded: this.filtersExpanded,
            allColumnKeys: this.allColumns.map(c => (c.key)),
            selectedColumnKeys: this.selectedColumns
        }
        // console.debug('Saving state', state);
        this.tableStateService.setState(this.tableId, state);
    }

    goToDetail(organisation: Organisation): void {
        this.saveState();
        this.router.navigate(['/organisations', organisation.id]).then(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    }

    add(): void {
        this.saveState();
        this.router.navigate(['/organisations', 'new']);
    }

    delete(id: number): void {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Delete Event',
                message: 'Are you sure you want to delete this organisation?'
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.organisationService.delete(id).subscribe({
                    next: () => {
                        this.loadOrganisations();
                        this.snackBar.open('Organisation deleted', 'Close', { duration: 3000 });
                    },
                    error: msg => {
                        this.snackBar.open('Failed to delete organisation', 'Close', { duration: 3000 });
                        console.error('Delete failed:', msg)
                    }
                });
            }
        });
    }

    export(): void {

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Export list',
                message: 'The information exported will correspond to the current selection of visible columns and search filters',
                buttonText: {
                    ok: "Proceed",
                    cancel: "Cancel"
                }
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result) {

                const data: any[] = [];
                this.dataSource.sortData(this.dataSource.filteredData, this.dataSource.sort!).forEach(organisation => {
                    const enriched = {
                        ...organisation,
                        valid: organisation.validityStatus(),
                        archived: organisation.archived,
                        countries: organisation.countryCodes.map(c => this.countryNames().get(c)).join(', '),
                        member: membershipMap[organisation.membershipStatus],
                        supplier: organisation.isSupplier,
                    };
                    const row = mapObject(enriched, [...this.selectedColumns, 'archived']);
                    data.push(row);
                });
                const headers = Object.keys(data[0]);
                this.exportService.generateReport('organisations', { headers: headers, data: data }).subscribe({
                    next: (report) => {
                        console.debug('OK');
                        const ts = DateTime.now().toFormat("yyyyMMdd-HHmmss");
                        let filename = `nexus_organisations_${ts}.xlsx`;
                        const url = window.URL.createObjectURL(report);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = filename;
                        a.click();
                        window.URL.revokeObjectURL(url);
                    },
                    error: (error) => {
                        console.error('Failed to generate report', error);
                        this.snackBar.open('Failed to generate report', 'Close', { duration: 3000 });
                    }
                })
            }
        })
    }



    openColumnSelector(): void {
        const dialogRef = this.dialog.open(ColumnSelectorDialogComponent, {
            data: {
                allColumns: this.allColumns,
                selected: [...this.selectedColumns],
                defaultSelection: [...this.defaultSelectedColumns],
                defaultOrder: [...this.defaultColumnsOrder]
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.selectedColumns = result.selected;
                this.allColumns = result.allColumns;
                this.saveState();
            }
        });
    }

    getDefaultFilters(): OrganisationFilters {
        return {
            textFilter: '',
            stakeholderGroups: createAdvancedSelectValue(),
            projects: createAdvancedSelectValue(),
            initiatives: createAdvancedSelectValue(),
            topics: createAdvancedSelectValue(),
            audiences: createAdvancedSelectValue(),
            memberTypes: createAdvancedSelectValue(),
            countries: createAdvancedSelectValue(),
            showActiveItems: true,
            showArchivedItems: false,
        };
    }

    clearAllFilters() {
        console.debug('clear all filters')
        this.resetCounter++; // increment → triggers ngOnChanges in children
        this.filterForm.reset();
    }

    toggleFilterMatch(fieldName: keyof OrganisationFilters) {
        this.filterForm.get(fieldName)?.setValue(!this.filterForm.get(fieldName)?.value);
    }

    togglePanel(event: MouseEvent) {
        event.stopPropagation(); // prevent header click from toggling
        this.filtersExpanded = !this.filtersExpanded;
    }

    /**
     * creates the template for the table columns
     */
    get gridTemplate() {
        return getGridTemplate(this.selectedColumns, this.allColumns);
    }

    getMembershipType(organisation: Organisation): MemberType | null {
        const activeMembershipRecords = organisation.getActiveMembershipRecords();
        if (activeMembershipRecords.length > 0) {
            return activeMembershipRecords[0].memberType ?? null;
        }
        return null;
    }

    /**
     *
     * @param org
     * @param filter
     * @returns
     */

    // I have to defined it this way so that the this context is preserved

    filterFunction = (org: Organisation, filter: string) => {
        const rawFilters = JSON.parse(filter);
        const filters: OrganisationFilters = {
            ...rawFilters,
        };

        if (!filters.showActiveItems && !org.archived) {
            return false;
        }

        if (!filters.showArchivedItems && org.archived) {
            return false;
        }

        if (filters?.textFilter) {
            const searchStr = filters.textFilter.toLowerCase();
            const inName = org.name?.toLowerCase().includes(searchStr);
            const inAcronym = org.acronym?.toLowerCase().includes(searchStr);
            if (!inName && !inAcronym) {
                return false;
            }
        }

        if (filters?.stakeholderGroups?.selectedValues && filters.stakeholderGroups?.selectedValues.length > 0) {
            const intersection = org.stakeholderGroups.filter(item => filters.stakeholderGroups?.selectedValues.includes(String(item.id)));
            if (filters.stakeholderGroups?.matchAll) {
                if (intersection.length < filters.stakeholderGroups?.selectedValues.length) {
                    return false
                }
            } else if (intersection.length === 0) {
                return false;
            }
        }
        if (filters?.projects?.selectedValues && filters.projects?.selectedValues.length > 0) {
            const intersection = org.projects.filter(item => filters.projects?.selectedValues.includes(String(item.id)));
            if (filters.projects?.matchAll) {
                if (intersection.length < filters.projects?.selectedValues.length) {
                    return false
                }
            } else if (intersection.length === 0) {
                return false;
            }
        }
        if (filters?.initiatives?.selectedValues && filters.initiatives?.selectedValues.length > 0) {
            const intersection = org.initiatives.filter(item => filters.initiatives?.selectedValues.includes(String(item.id)));
            if (filters.initiatives?.matchAll) {
                if (intersection.length < filters.initiatives?.selectedValues.length) {
                    return false
                }
            } else if (intersection.length === 0) {
                return false;
            }
        }

        // There are two types of topics: General and Member
        if (filters?.topics?.selectedValues && filters.topics?.selectedValues.length > 0) {
            const selectedFilters = filters.topics.selectedValues ?? [];
            const selectedGeneralFiltersCount = selectedFilters.map(v => Number(v)).filter(id => this.generalTopicIds().includes(id)).length;
            const selectedMemberFiltersCount = selectedFilters.map(v => Number(v)).filter(id => this.memberTopicIds().includes(id)).length;

            const commonGeneralTopics = org.topics.filter(topic =>
                selectedFilters.includes(String(topic.id)) &&
                this.generalTopicIds().includes(topic.id)
            );

            const allMemberTopics = org.getActiveMembershipRecords()
                .flatMap(r => r.topics);
            const commonMemberTopics = allMemberTopics.filter(topic =>
                selectedFilters.includes(String(topic.id)) &&
                this.memberTopicIds().includes(topic.id)
            );

            // If user sets values in both filters, should I consider a match if either filter match or if both match?
            // This is the 'both' case
            if (((selectedGeneralFiltersCount > 0) && (commonGeneralTopics.length === 0)) ||
                ((selectedMemberFiltersCount > 0) && (commonMemberTopics.length === 0))) {
                return false;
            }
        }

        if (filters?.audiences?.selectedValues && filters.audiences?.selectedValues.length > 0) {
            const intersection = org.targetAudiences.filter(item => filters.audiences?.selectedValues.includes(String(item.id)));
            if (filters.audiences?.matchAll) {
                if (intersection.length < filters.audiences?.selectedValues.length) {
                    return false
                }
            } else if (intersection.length === 0) {
                return false;
            }
        }

        if (filters?.memberTypes?.selectedValues && filters.memberTypes?.selectedValues.length > 0) {
            const matches = org.getActiveMembershipRecords().some(record => {
                if (record.memberType) {
                    return filters.memberTypes?.selectedValues.includes(record.memberType);
                }
                return false;
            });
            if (!matches) {
                return false;
            }
        }

        if (filters?.countries.selectedValues && filters.countries.selectedValues.length > 0) {
            const distinctOrgCountries = new Set((org.addresses ?? []).filter(a => a.countryIsoCode != null).map(a => a.countryIsoCode));
            const matches = filters.countries.selectedValues.some(code => distinctOrgCountries.has(code));
            if (!matches) {
                return false;
            }
        }
        return true;
    }

    getAllCountries(organisation: Organisation): string[] {
        return [
            ...new Set(
                (organisation.addresses ?? [])
                    .flatMap(item => item.countryIsoCode ? [item.countryIsoCode] : [])
            )
        ];
    }

}
